// デフォルトのテーマ
import '@splidejs/react-splide/css'

import { Options, Splide, SplideSlide } from '@splidejs/react-splide'
import { CheckCircle, Eye, EyeOff, Highlighter, Info, List, Settings, X } from 'lucide-react'
import { type CSSProperties, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { BadgeIcon } from '@/components/shared/badge-icon'
import { Footer } from '@/components/shared/footer'
import { Header, type ToolbarItem } from '@/components/shared/header'
import { ActionSection, RadioSection } from '@/components/ui/custom-dropdown'
import { FOOTER_HEIGHT, HEADER_HEIGHT, TOOLBAR_HEIGHT } from '@/lib/constants'
import { useAppStore } from '@/store/useAppStore'
import { useProgressStore } from '@/store/useProgressStore'
import { useComicLayoutStore } from '@/store/useReaderLayoutStore'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { HighlightListDialog } from '../highlight/highlight-list-dialog'
import { HighlightMemoDialog } from '../highlight/highlight-memo-dialog'
import { HighlightSheet } from '../highlight/highlight-sheet'
import { useImageHighlight } from '../hooks/useImageHighlight'
import {
  DEFAULT_IMAGE_READER_SETTINGS,
  type ImageReaderSettings,
  useImageReaderSettingsQuery,
} from '../hooks/useImageReaderSettingsQuery'
import { usePageSwipe } from '../hooks/usePageSwipe'
import { useRememberedLayout } from '../hooks/useRememberedLayout'
import { syncSplideIndex, useSplideScrollAnchor } from '../hooks/useSplideScrollAnchor'
import { SWIPE_BACK_ANIMATION_MS, useSwipeBack } from '../hooks/useSwipeBack'
import { useSwipeHandler } from '../hooks/useSwipeHandler'
import { CustomScrollbar } from '../shared/custom-scrollbar'
import { LayoutPrompt } from '../shared/layout-prompt'
import { ReaderLayoutFields } from '../shared/reader-layout-fields'
import { SwipeProgressIndicator } from '../shared/swipe-progress-indicator'
import { ImagePage } from './components/image-page'
import { ImageReaderSettingsDialog } from './components/image-reader-settings-dialog'

const DEFAULT_OPTIONS: Options = {
  drag: 'free',
  pagination: false,
  arrows: false,
  gap: '0px',
  speed: 0,
}
// 表示領域の高さ。フッター固定時はフッター分を差し引いた値をルート要素で定義する
const READER_VH = 'var(--reader-vh)'
const TTB_OPTIONS: Options = {
  ...DEFAULT_OPTIONS,
  direction: 'ttb',
  autoHeight: true,
  autoWidth: false,
  height: '100%',
  width: '100vw',
}
const LTR_OPTIONS: Options = {
  ...DEFAULT_OPTIONS,
  direction: 'ltr',
  autoWidth: true,
  height: READER_VH,
  width: '100%',
}
const RTL_OPTIONS: Options = {
  ...DEFAULT_OPTIONS,
  direction: 'rtl',
  autoWidth: true,
  height: READER_VH,
  width: '100%',
}

// ページモードと表示方向に応じた画像クラス名を取得
const getImageClassName = (
  pageMode: 'single' | 'dual' | 'dual-cover',
  direction: 'ttb' | 'rtl' | 'ltr',
  dragMode: 'free' | 'snap',
  isSingleInGroup: boolean,
): string => {
  // シングルページモードまたはグループ内で単一画像の場合
  if (pageMode === 'single' || isSingleInGroup) {
    if (dragMode === 'snap') {
      return 'block mx-auto h-(--reader-vh) object-contain'
    }
    return direction === 'ttb' ? 'mx-none w-screen object-contain' : 'mx-none h-(--reader-vh) object-contain'
  }

  // デュアルページモード
  if (direction === 'ttb') {
    // TTB: 横並び2枚、各画像は画面幅の半分
    return 'w-full h-auto object-contain'
  }
  // RTL/LTR: 横並び2枚、各画像は画面幅の半分、高さは画面いっぱい
  return 'h-(--reader-vh) w-[50vw] object-contain'
}

type Props = {
  images: (string | null)[]
  onClose: () => void
  title: string
  episodeId?: number // 進捗保存用（オプション）
  episodeIndex?: number // エピソード番号（ヘッダー表示用）
  productId?: number // お気に入り・削除用
  isFavorite?: boolean // お気に入り状態
  onFavoriteToggle?: () => void // お気に入りトグル
  onDelete?: () => void // 削除
  onInfoClick?: () => void
  onNext?: () => void
  onPrev?: () => void
  loadedCount?: number
  totalCount?: number
  isStreamComplete?: boolean
  onPageChange?: (pageIndex: number) => void // ページ変更時のコールバック（優先読み込み用）
  initialPageIndex?: number // 復元するページ（0ベース、呼び出し側で進捗から求める）
}

export const ImageReader = ({
  images,
  onClose,
  title,
  episodeId,
  episodeIndex,
  productId,
  isFavorite,
  onFavoriteToggle,
  onDelete,
  onInfoClick,
  onNext,
  onPrev,
  loadedCount,
  totalCount,
  isStreamComplete,
  onPageChange,
  initialPageIndex,
}: Props) => {
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)

  // 進捗ストア
  const { saveEpisodeProgress } = useProgressStore()

  // 表示設定（URL クエリで保持し、リロード・共有時に復元できるようにする）
  // URL に無い項目は、作品ごとに覚えた設定 → 最後に選んだ設定 → 初期値の順に使う
  const {
    initialSettings,
    isPromptOpen: isLayoutPromptOpen,
    closePrompt: closeLayoutPrompt,
    save: saveLayout,
  } = useRememberedLayout(useComicLayoutStore, productId, DEFAULT_IMAGE_READER_SETTINGS)
  const [viewerSettings, setViewerSettings] = useImageReaderSettingsQuery(initialSettings)
  const { direction, dragMode, pageMode, dualReadingOrder } = viewerSettings
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)

  const [currentIndex, setCurrentIndex] = useState(0) // スライドインデックス（グループ単位）
  const [menuVisible, setMenuVisible] = useState(false)
  const isFooterPinned = useUISettingsStore((state) => state.isReaderFooterPinned)
  const pageTurnAnimation = useUISettingsStore((state) => state.pageTurnAnimation)
  const isInstantTurn = dragMode === 'snap' && !pageTurnAnimation

  const [initialRender, setInitialRender] = useState(false)
  const splideRef = useRef<Splide>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const progressSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedIndexRef = useRef<number | null>(initialPageIndex ?? null)

  // ハイライトフック
  const {
    highlights,
    highlightListOpen,
    highlightMemoOpen,
    editingHighlight,
    isSelectionMode,
    highlightStartPoint,
    highlightsVisible,
    setHighlights,
    setHighlightListOpen,
    setHighlightMemoOpen,
    setEditingHighlight,
    setPendingSelection,
    handleCreateHighlight,
    handleUpdateHighlight,
    handleDeleteHighlight,
    handleHighlightClick,
    handleCancelSelection,
    handleImageInteraction,
    handleOpenHighlightList,
    handleHighlightNavigate: navigateToHighlight,
    startSelectionMode,
    toggleHighlightsVisible,
  } = useImageHighlight({ episodeId })

  // タッチデバイスかどうかを判定
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  // 復元待ちのページインデックス（groupedImages計算後に適用し、適用後は null）
  const [savedPageIndex, setSavedPageIndex] = useState<number | null>(initialPageIndex ?? null)
  const isRestoringProgress = savedPageIndex !== null

  // 未読み込みページの仮サイズ。見開き等の外れ値に引っ張られないよう、読み込めた画像の縦横比の中央値を使う
  const imageRatiosRef = useRef(new Map<string, number>())
  const [placeholderRatio, setPlaceholderRatio] = useState<number | null>(null)
  const handleImageLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight, src } = e.currentTarget
    if (!naturalWidth || !naturalHeight) return
    imageRatiosRef.current.set(src, naturalWidth / naturalHeight)
    const ratios = [...imageRatiosRef.current.values()].sort((a, b) => a - b)
    const median = ratios.at(Math.floor(ratios.length / 2)) ?? null
    // 小さな揺れで全ページの仮サイズを変えないよう丸める
    setPlaceholderRatio(median && Math.round(median * 100) / 100)
  }, [])
  const placeholderStyle = useMemo<CSSProperties | undefined>(
    () => (placeholderRatio ? { aspectRatio: `auto ${placeholderRatio}` } : undefined),
    [placeholderRatio],
  )

  // スクロール完了時に即座に保存（重複防止付き）
  // ページインデックス（0ベース）を受け取り、1ベースに変換してAPI送信
  const saveProgressImmediate = useCallback(
    (pageIndex: number) => {
      if (!episodeId) return
      if (lastSavedIndexRef.current === pageIndex) return

      saveEpisodeProgress(episodeId, pageIndex + 1)
      lastSavedIndexRef.current = pageIndex
    },
    [episodeId, saveEpisodeProgress],
  )

  // ハイライトへのナビゲーション
  const handleHighlightNavigate = useCallback(
    (highlightId: number) => {
      navigateToHighlight(highlightId, splideRef, setCurrentIndex)
    },
    [navigateToHighlight],
  )

  // 選択モード開始（メニューを閉じる）
  const handleStartSelectionMode = useCallback(() => {
    startSelectionMode()
    setMenuVisible(false)
  }, [startSelectionMode])

  // メモ化されたonNextハンドラー
  const handleNext = useCallback(() => {
    if (!onNext) return
    // スクロール位置をトップに戻す
    if (splideRef.current) {
      splideRef.current.go(0)
    }
    setMenuVisible(false)
    // 元のonNext関数を実行
    onNext()
  }, [onNext])

  // メモ化されたonPrevハンドラー
  const handlePrev = useCallback(() => {
    if (!onPrev) return
    setMenuVisible(false)
    onPrev()
  }, [onPrev])

  // 実効pageMode: RTL/LTRではSnapモード時のみdual系が有効
  const effectivePageMode = useMemo(() => {
    if (direction === 'ttb') {
      return pageMode // TTBでは常に有効
    }
    // RTL/LTRではSnapモード時のみdual系が有効
    if (dragMode === 'snap') {
      return pageMode
    }
    return 'single'
  }, [direction, dragMode, pageMode])

  // 画像をグループ化: dual-coverは[[0], [1,2], [3,4], ...]、dualは[[0,1], [2,3], ...]、singleは[[0], [1], ...]
  const groupedImages = useMemo(() => {
    if (effectivePageMode === 'single') {
      return images.map((img, idx) => ({ images: [img], startPageIndex: idx }))
    }
    if (effectivePageMode === 'dual-cover') {
      // 最初の1枚は単独、以降は2枚ずつ
      const groups: { images: (string | null)[]; startPageIndex: number }[] = []
      const firstImage = images.at(0)
      groups.push({ images: [firstImage ?? null], startPageIndex: 0 })
      for (let i = 1; i < images.length; i += 2) {
        const currentImage = images.at(i)
        const pair: (string | null)[] = [currentImage ?? null]
        if (i + 1 < images.length) {
          const nextImage = images.at(i + 1)
          pair.push(nextImage ?? null)
        }
        groups.push({ images: pair, startPageIndex: i })
      }
      return groups
    }
    // dual: 2枚ずつ
    const groups: { images: (string | null)[]; startPageIndex: number }[] = []
    for (let i = 0; i < images.length; i += 2) {
      const currentImage = images.at(i)
      const pair: (string | null)[] = [currentImage ?? null]
      if (i + 1 < images.length) {
        const nextImage = images.at(i + 1)
        pair.push(nextImage ?? null)
      }
      groups.push({ images: pair, startPageIndex: i })
    }
    return groups
  }, [images, effectivePageMode])

  // ホイールスクロールでページ移動（debounce用）
  const lastWheelTimeRef = useRef<number>(0)

  // 滑らかなホイールスクロール（freeモード）/ ページ単位移動（snapモード）
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleWheel = (e: WheelEvent) => {
      const splide = splideRef.current?.splide
      if (!splide) return

      e.preventDefault()

      // snapモードまたはdual系の場合はページ単位で移動
      if (dragMode === 'snap' || effectivePageMode !== 'single') {
        const now = Date.now()
        // 連続スクロールを防ぐためのdebounce（150ms）
        if (now - lastWheelTimeRef.current < 150) {
          return
        }
        lastWheelTimeRef.current = now

        const delta = direction === 'ttb' ? e.deltaY : e.deltaX || e.deltaY
        const totalSlides = groupedImages.length

        if (delta > 0) {
          // 下/右にスクロール → 次のページ
          const newIndex = Math.min(totalSlides - 1, currentIndex + 1)
          if (newIndex !== currentIndex) {
            splideRef.current?.go(newIndex)
            setCurrentIndex(newIndex)
          }
        } else if (delta < 0) {
          // 上/左にスクロール → 前のページ
          const newIndex = Math.max(0, currentIndex - 1)
          if (newIndex !== currentIndex) {
            splideRef.current?.go(newIndex)
            setCurrentIndex(newIndex)
          }
        }
        return
      }

      // freeモード: 滑らかなスクロール
      // Splideの内部コンポーネントを取得
      const Move = splide.Components.Move
      const Layout = splide.Components.Layout
      const Slides = splide.Components.Slides
      const currentPosition = Move.getPosition()

      // direction に応じてdeltaを適用
      const delta = direction === 'ttb' ? e.deltaY : e.deltaX || e.deltaY
      const sensitivity = 2 // スクロール感度調整

      // スクロール可能な範囲を計算
      // listSize: 表示領域のサイズ、sliderSize: 全スライドの合計サイズ
      const listSize = Layout.listSize()
      const sliderSize = Layout.sliderSize()
      const maxScroll = -(sliderSize - listSize) // 最大スクロール量（負の値）

      let newPosition = currentPosition - delta * sensitivity

      // 位置を制限内にクランプ (0が開始位置、maxScrollが終了位置)
      newPosition = Math.min(0, Math.max(maxScroll, newPosition))

      Move.translate(newPosition)
      syncSplideIndex(splide)

      // 現在表示中のスライドインデックスを計算してスクロールバーに反映
      const slides = Slides.get()
      const totalSlides = slides.length

      // 最後までスクロールした場合は最後のインデックスを設定
      if (newPosition <= maxScroll + 10) {
        setCurrentIndex(totalSlides - 1)
        return
      }

      const absPosition = Math.abs(newPosition)

      // 各スライドの位置を計算し、画面内に「見えている」最後のスライドを検出
      let visibleIndex = 0
      let accumulatedSize = 0

      for (let i = 0; i < totalSlides; i++) {
        const slide = slides.at(i)
        if (!slide) continue
        const slideSize = direction === 'ttb' ? slide.slide.offsetHeight : slide.slide.offsetWidth
        const slideStart = accumulatedSize
        const slideEnd = accumulatedSize + slideSize

        // スライドが画面内に見えているか判定
        // 画面の表示範囲: absPosition ~ absPosition + listSize
        const isVisible = slideStart < absPosition + listSize && slideEnd > absPosition

        if (isVisible) {
          visibleIndex = i
        }

        accumulatedSize += slideSize
      }

      setCurrentIndex(visibleIndex)
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => container.removeEventListener('wheel', handleWheel)
  }, [direction, dragMode, effectivePageMode, currentIndex, groupedImages.length])

  // スライドインデックス → 実際のページインデックスに変換（進捗保存用）
  const slideIndexToPageIndex = useCallback(
    (slideIndex: number): number => {
      if (slideIndex < 0 || slideIndex >= groupedImages.length) return 0
      const group = groupedImages.at(slideIndex)
      if (!group) return 0
      // グループの最後のページインデックスを返す（そこまで見たとする）
      return group.startPageIndex + group.images.length - 1
    },
    [groupedImages],
  )

  // 実際のページインデックス → スライドインデックスに変換（進捗復元用）
  const pageIndexToSlideIndex = useCallback(
    (pageIndex: number): number => {
      for (let i = 0; i < groupedImages.length; i++) {
        const group = groupedImages.at(i)
        if (!group) continue
        const endIndex = group.startPageIndex + group.images.length - 1
        if (pageIndex <= endIndex) {
          return i
        }
      }
      return groupedImages.length - 1
    },
    [groupedImages],
  )

  // 保存された進捗を適用（groupedImages計算後）
  useEffect(() => {
    if (savedPageIndex === null || !splideRef.current) return
    // ZipReader は images を effect で渡すため、マウント直後は空配列（スライド0枚）のことがある
    if (groupedImages.length === 0) return

    const slideIndex = pageIndexToSlideIndex(savedPageIndex)
    splideRef.current.go(slideIndex)
    setCurrentIndex(slideIndex)
    setSavedPageIndex(null) // 一度適用したらクリア
  }, [savedPageIndex, pageIndexToSlideIndex, groupedImages.length])

  const splideAnchorHandlers = useSplideScrollAnchor({
    splideRef,
    enabled: dragMode === 'free',
    direction,
    slideCount: groupedImages.length,
  })

  // ストリーミング完了時にSplideをリフレッシュ（画像表示を更新）
  useEffect(() => {
    if (!isStreamComplete) return
    if (!splideRef.current?.splide) return

    // 少し遅延してリフレッシュ（DOMの更新を待つ）
    const timer = setTimeout(() => {
      const splide = splideRef.current?.splide
      if (!splide) return
      // refresh は Splide.index（スライド先頭）へ jump するため、スライド途中の位置を退避・復元する
      const { Move } = splide.Components
      const position = Move.getPosition()
      splide.refresh()
      Move.translate(position)
      if (Move.exceededLimit()) Move.translate(Move.getLimit(Move.exceededLimit(true)))
    }, 100)

    return () => clearTimeout(timer)
  }, [isStreamComplete])

  // 進捗保存（debounce: 1秒）- スクロール中の高頻度保存を防止
  useEffect(() => {
    if (!episodeId) return
    // 復元前の currentIndex=0 を保存すると進捗が先頭ページで上書きされる
    if (isRestoringProgress) return

    // 既存のタイマーをクリア
    if (progressSaveTimeoutRef.current) {
      clearTimeout(progressSaveTimeoutRef.current)
    }

    // 現在のスライドインデックスを実ページインデックスに変換
    const pageIndex = slideIndexToPageIndex(currentIndex)

    // 1秒後に進捗保存（同じインデックスなら保存しない）
    progressSaveTimeoutRef.current = setTimeout(() => {
      saveProgressImmediate(pageIndex)
    }, 1000)

    return () => {
      if (progressSaveTimeoutRef.current) {
        clearTimeout(progressSaveTimeoutRef.current)
      }
    }
  }, [currentIndex, episodeId, slideIndexToPageIndex, saveProgressImmediate, isRestoringProgress])

  // 定期保存（30秒ごと）- 履歴はバックエンドで自動管理
  useEffect(() => {
    if (!episodeId) return
    if (isRestoringProgress) return

    const interval = setInterval(() => {
      const pageIndex = slideIndexToPageIndex(currentIndex)
      saveEpisodeProgress(episodeId, pageIndex + 1)
    }, 30000)

    return () => clearInterval(interval)
  }, [currentIndex, episodeId, slideIndexToPageIndex, saveEpisodeProgress, isRestoringProgress])

  // ページ変更時のコールバック（優先読み込み用）
  useEffect(() => {
    if (!onPageChange) return
    // 復元前の currentIndex=0 を通知すると先頭ページの読み込みが割り込む
    if (isRestoringProgress) return
    const pageIndex = slideIndexToPageIndex(currentIndex)
    onPageChange(pageIndex)
  }, [currentIndex, slideIndexToPageIndex, onPageChange, isRestoringProgress])

  // 最後のページかどうかを判定（スライド単位）
  const isAtEnd = useMemo(() => {
    return currentIndex === groupedImages.length - 1
  }, [currentIndex, groupedImages.length])

  // キーボードナビゲーション
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 入力フィールドにフォーカスがある場合は無視
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      const splide = splideRef.current
      if (!splide) return

      const totalSlides = groupedImages.length
      let newIndex: number | null = null

      if (direction === 'ttb') {
        // TTB: 上下キーで操作
        if (e.key === 'ArrowUp') {
          newIndex = Math.max(0, currentIndex - 1)
        } else if (e.key === 'ArrowDown') {
          newIndex = Math.min(totalSlides - 1, currentIndex + 1)
        }
      } else if (direction === 'rtl') {
        // RTL: 右キーで前、左キーで次
        if (e.key === 'ArrowRight') {
          newIndex = Math.max(0, currentIndex - 1)
        } else if (e.key === 'ArrowLeft') {
          newIndex = Math.min(totalSlides - 1, currentIndex + 1)
        }
      } else {
        // LTR: 左キーで前、右キーで次
        if (e.key === 'ArrowLeft') {
          newIndex = Math.max(0, currentIndex - 1)
        } else if (e.key === 'ArrowRight') {
          newIndex = Math.min(totalSlides - 1, currentIndex + 1)
        }
      }

      // 上下キーはTTB以外でも使えるようにする（追加操作）
      if (direction !== 'ttb') {
        if (e.key === 'ArrowUp') {
          newIndex = Math.max(0, currentIndex - 1)
        } else if (e.key === 'ArrowDown') {
          newIndex = Math.min(totalSlides - 1, currentIndex + 1)
        }
      }

      if (newIndex !== null && newIndex !== currentIndex) {
        e.preventDefault()
        splide.go(newIndex)
        setCurrentIndex(newIndex)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [direction, currentIndex, groupedImages.length])

  // スワイプ処理フックを使用
  const { swipeHandler, swipeState } = useSwipeHandler({
    direction,
    isAtEnd,
    onNext: handleNext,
  })

  // TTBモード: 右スワイプで戻るフック
  const { swipeBackRef, swipeBackState } = useSwipeBack({
    direction,
    onClose,
  })

  const options = useMemo((): Options => {
    const d = direction === 'ttb' ? TTB_OPTIONS : direction === 'rtl' ? RTL_OPTIONS : LTR_OPTIONS
    if (dragMode === 'free') {
      return d
    }
    // アニメーションなしでは指に追従させず、usePageSwipe で一瞬でめくる
    const turn: Options = isInstantTurn ? { drag: false, speed: 0 } : { drag: true, speed: 400 }
    if (direction === 'ttb') {
      return {
        ...d,
        ...turn,
        perPage: 1,
        height: READER_VH,
        autoHeight: false,
      }
    }
    // RTL/LTR + Snap: perPage: 1（グループ化済みなので1スライド=1-2ページ）
    return { ...d, ...turn, perPage: 1, autoWidth: false }
  }, [direction, dragMode, isInstantTurn])

  const turnToNextPage = useCallback(() => splideRef.current?.go('>'), [])
  const turnToPrevPage = useCallback(() => splideRef.current?.go('<'), [])
  usePageSwipe({
    targetRef: containerRef,
    enabled: isInstantTurn,
    axis: direction === 'ttb' ? 'y' : 'x',
    reversed: direction === 'rtl',
    onNext: turnToNextPage,
    onPrev: turnToPrevPage,
  })

  // スクロールバーから位置を変更
  const handleScrollChange = useCallback((index: number) => {
    if (splideRef.current) {
      splideRef.current.go(index)
    }
  }, [])

  // 最初のページへ移動
  const handleGoToFirstPage = useCallback(() => {
    if (splideRef.current) {
      splideRef.current.go(0)
      setCurrentIndex(0)
    }
  }, [])

  // 最後のページへ移動（スライド単位）
  const handleGoToLastPage = useCallback(() => {
    if (splideRef.current) {
      const lastIndex = groupedImages.length - 1
      splideRef.current.go(lastIndex)
      setCurrentIndex(lastIndex)
    }
  }, [groupedImages.length])

  const applySettings = useCallback(
    (next: ImageReaderSettings) => {
      setInitialRender(true)
      setViewerSettings(next)
      saveLayout(next)
    },
    [setViewerSettings, saveLayout],
  )
  const handleSettingsChange = useCallback(
    (next: ImageReaderSettings) => {
      applySettings(next)
      closeLayoutPrompt()
    },
    [applySettings, closeLayoutPrompt],
  )
  const handleLayoutPromptDone = useCallback(() => {
    saveLayout(viewerSettings)
    closeLayoutPrompt()
  }, [saveLayout, viewerSettings, closeLayoutPrompt])

  // ハイライト一覧を開く（メニューを閉じる）
  const handleOpenHighlightListWithMenu = useCallback(() => {
    handleOpenHighlightList()
    setMenuVisible(false)
  }, [handleOpenHighlightList])

  const dropdownSections: (RadioSection | ActionSection)[] = useMemo(
    () => [
      {
        type: 'action' as const,
        label: 'Actions',
        items: [
          {
            key: 'first-page',
            label: '⏮ 最初のページ',
            onClick: handleGoToFirstPage,
          },
          {
            key: 'last-page',
            label: '⏭ 最後のページ',
            onClick: handleGoToLastPage,
          },
          {
            key: 'info',
            label: 'Open info',
            onClick: onInfoClick,
          },
          ...(episodeId
            ? [
                {
                  key: 'highlights',
                  label: `📝 ハイライト一覧 (${highlights.length})`,
                  onClick: handleOpenHighlightListWithMenu,
                },
                {
                  key: 'toggle-highlights',
                  label: highlightsVisible ? '👁️ ハイライト非表示' : '👁️‍🗨️ ハイライト表示',
                  onClick: toggleHighlightsVisible,
                },
              ]
            : []),
          ...(productId && onFavoriteToggle
            ? [
                {
                  key: 'favorite',
                  label: isFavorite ? '⭐ お気に入り解除' : '☆ お気に入り追加',
                  onClick: onFavoriteToggle,
                },
              ]
            : []),
          ...(productId && onDelete && hasFullAccess
            ? [
                {
                  key: 'delete',
                  label: '🗑️ 削除',
                  onClick: onDelete,
                },
              ]
            : []),
        ],
      },
    ],
    [
      handleGoToFirstPage,
      handleGoToLastPage,
      onInfoClick,
      episodeId,
      highlights.length,
      handleOpenHighlightListWithMenu,
      highlightsVisible,
      toggleHighlightsVisible,
      productId,
      onFavoriteToggle,
      isFavorite,
      onDelete,
      hasFullAccess,
    ],
  )

  // Toolbar items
  const toolbarItems: ToolbarItem[] = useMemo(
    () => [
      ...(episodeId
        ? [
            {
              icon: isSelectionMode ? <X size={20} /> : <Highlighter size={20} />,
              label: isSelectionMode ? 'モード終了' : 'ハイライト開始',
              onClick: () => {
                if (isSelectionMode) {
                  handleCancelSelection()
                } else {
                  handleStartSelectionMode()
                }
              },
              show: true,
              className: isSelectionMode ? 'bg-yellow-500/20' : '',
            },
            {
              icon: <BadgeIcon MainIcon={Highlighter} BadgeIcon={List} />,
              label: 'ハイライト一覧',
              onClick: handleOpenHighlightListWithMenu,
              show: true,
            },
            {
              icon: <BadgeIcon MainIcon={Highlighter} BadgeIcon={highlightsVisible ? Eye : EyeOff} />,
              label: highlightsVisible ? 'ハイライト非表示' : 'ハイライト表示',
              onClick: toggleHighlightsVisible,
              show: true,
              className: !highlightsVisible ? 'opacity-50' : '',
            },
          ]
        : []),
      {
        icon: <Settings size={20} />,
        label: '設定',
        onClick: () => setSettingsDialogOpen(true),
      },
      {
        icon: <Info size={20} />,
        label: '詳細情報',
        onClick: () => onInfoClick?.(),
        show: !!onInfoClick,
      },
    ],
    [
      onInfoClick,
      episodeId,
      handleOpenHighlightListWithMenu,
      isSelectionMode,
      handleCancelSelection,
      handleStartSelectionMode,
      highlightsVisible,
      toggleHighlightsVisible,
    ],
  )

  // スワイプバック背景の不透明度（0〜1: スワイプ距離に比例）
  const swipeBackProgress =
    swipeBackState.isSwiping || swipeBackState.isAnimating
      ? Math.min(1, swipeBackState.translateX / window.innerWidth)
      : 0

  return (
    <div
      ref={swipeBackRef}
      className="w-full h-screen fixed"
      style={{ '--reader-vh': isFooterPinned ? `calc(100vh - ${FOOTER_HEIGHT})` : '100vh' } as CSSProperties}
    >
      {/* スワイプバック時の暗い背景（距離に応じて透明に → 裏のProductContainerが見える） */}
      <div
        className="fixed inset-0 z-[-1] bg-black pointer-events-none transition-opacity"
        style={{ opacity: swipeBackState.isSwiping || swipeBackState.isAnimating ? 1 - swipeBackProgress : 1 }}
      />
      <div
        {...swipeHandler}
        ref={containerRef}
        key={direction + dragMode}
        className="w-full h-(--reader-vh) fixed"
        style={{
          transform: swipeBackState.translateX > 0 ? `translateX(${swipeBackState.translateX}px)` : undefined,
          transition: swipeBackState.isAnimating ? `transform ${SWIPE_BACK_ANIMATION_MS}ms ease-out` : undefined,
        }}
        onClick={isTouchDevice ? undefined : () => setMenuVisible((prev) => !prev)}
        onDoubleClick={isTouchDevice ? () => setMenuVisible((prev) => !prev) : undefined}
      >
        <Splide
          ref={splideRef}
          aria-label="Reader"
          options={options}
          className="w-full h-full"
          onDrag={splideAnchorHandlers.onDrag}
          onDragging={splideAnchorHandlers.onDragging}
          onDragged={splideAnchorHandlers.onDragged}
          onActive={(e) => {
            // TODO 遷移時のアニメーションを一時的に削除
            if (e.index === 0 && !initialRender) {
              e.go(currentIndex)
            }
            setCurrentIndex(e.index)
          }}
          onMoved={(_, newIndex) => {
            // スクロール完了時に即座に保存（実ページ番号に変換）
            const pageIndex = slideIndexToPageIndex(newIndex)
            saveProgressImmediate(pageIndex)
          }}
        >
          {groupedImages.map((group, slideIndex) => (
            <SplideSlide key={slideIndex + 'slide'}>
              <div
                dir={
                  direction === 'rtl' && effectivePageMode !== 'single' && group.images.length > 1 ? 'ltr' : undefined
                }
                className={`relative ${
                  // TTBでdual系の場合はgrid-cols-2、RTL/LTRではflex
                  effectivePageMode !== 'single' && group.images.length > 1
                    ? direction === 'ttb'
                      ? 'grid grid-cols-2 gap-0 w-full'
                      : 'flex flex-row justify-center h-(--reader-vh)'
                    : ''
                }`}
              >
                {/* RTLモードまたはTTB+RTL読み方向の場合はグループ内の画像を逆順でレンダリング（右が小さいページ番号） */}
                {
                  // RTL/LTRモードの場合: direction === 'rtl' なら逆順
                  ((direction === 'rtl' && group.images.length > 1) ||
                  // TTBモードの場合: dualReadingOrder === 'rtl' なら逆順
                  (direction === 'ttb' && dualReadingOrder === 'rtl' && group.images.length > 1)
                    ? [...group.images].reverse().map((item, revIdx) => ({
                        item,
                        actualPageIndex: group.startPageIndex + (group.images.length - 1 - revIdx),
                      }))
                    : group.images.map((item, imgIdx) => ({
                        item,
                        actualPageIndex: group.startPageIndex + imgIdx,
                      }))
                  ).map(({ item, actualPageIndex }) => (
                    <ImagePage
                      key={actualPageIndex}
                      src={item}
                      pageIndex={actualPageIndex}
                      imageClassName={getImageClassName(
                        effectivePageMode,
                        direction,
                        dragMode,
                        group.images.length === 1,
                      )}
                      placeholderStyle={placeholderStyle}
                      highlights={highlights}
                      highlightsVisible={highlightsVisible}
                      startPoint={highlightStartPoint?.page === actualPageIndex ? highlightStartPoint : null}
                      onInteraction={handleImageInteraction}
                      onHighlightClick={handleHighlightClick}
                      onImageLoad={handleImageLoad}
                    />
                  ))
                }
              </div>
            </SplideSlide>
          ))}
        </Splide>

        {/* ハイライト選択モード状態バナー */}
        {isSelectionMode && (
          <div className="fixed top-16 left-0 right-0 z-50 flex items-center justify-center">
            <div className="bg-primary/90 text-primary-foreground px-4 py-2 rounded-full shadow-lg flex items-center gap-3">
              {highlightStartPoint ? (
                <>
                  <CheckCircle size={20} />
                  <span className="text-sm font-medium">終了位置をタップ</span>
                  <button
                    onClick={handleCancelSelection}
                    className="ml-2 p-1 hover:bg-white/20 rounded-full transition-colors"
                    aria-label="キャンセル"
                  >
                    <X size={16} />
                  </button>
                </>
              ) : (
                <>
                  <Highlighter size={20} />
                  <span className="text-sm font-medium">開始位置をタップ</span>
                  <button
                    onClick={handleCancelSelection}
                    className="ml-2 p-1 hover:bg-white/20 rounded-full transition-colors"
                    aria-label="キャンセル"
                  >
                    <X size={16} />
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* スワイプ進捗インジケーター（次のエピソードがある場合のみ） */}
        {onNext && <SwipeProgressIndicator progress={swipeState.progress} isVisible={swipeState.isSwiping} />}
      </div>

      {/* 画像読み込み状況（ストリーミング中のみ表示） */}
      {loadedCount !== undefined && totalCount !== undefined && !isStreamComplete && (
        <div className="fixed bottom-2 left-2 z-50 px-2 py-1 rounded bg-black/40 text-white/70 text-xs font-mono pointer-events-none">
          {loadedCount}/{totalCount}
        </div>
      )}

      {menuVisible && (
        <div>
          <Header
            title={episodeIndex !== undefined ? `${episodeIndex}話 ${title}` : title}
            onClose={onClose}
            dropdownSections={dropdownSections}
            toolbarItems={toolbarItems}
          />
          {/* Scrollbar */}
          <CustomScrollbar
            scrollValue={currentIndex}
            onScrollChange={handleScrollChange}
            max={groupedImages.length - 1}
            top={direction === 'ttb' ? `calc(${HEADER_HEIGHT} + ${TOOLBAR_HEIGHT})` : undefined}
            bottom={FOOTER_HEIGHT}
            height={
              direction === 'ttb' ? `calc(100vh - ${HEADER_HEIGHT} - ${TOOLBAR_HEIGHT} - ${FOOTER_HEIGHT})` : '60px'
            }
            orientation={direction === 'ttb' ? 'vertical' : 'horizontal'}
            inverted={direction === 'rtl'}
          />
        </div>
      )}

      {(menuVisible || isFooterPinned) && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <Footer
            currentIndex={slideIndexToPageIndex(currentIndex)}
            totalImages={totalCount ?? images.length}
            onPrev={onPrev ? handlePrev : undefined}
            onNext={onNext ? handleNext : undefined}
            direction={direction}
            pinnable
          />
        </div>
      )}

      {/* ハイライト一覧ダイアログ */}
      {episodeId && (
        <HighlightListDialog
          open={highlightListOpen}
          onClose={() => setHighlightListOpen(false)}
          episodeId={episodeId}
          onNavigate={handleHighlightNavigate}
          onHighlightsChange={setHighlights}
        />
      )}

      {/* ハイライトメモ入力ダイアログ */}
      <HighlightMemoDialog
        open={highlightMemoOpen}
        onClose={() => {
          setHighlightMemoOpen(false)
          setPendingSelection(null)
        }}
        onSave={handleCreateHighlight}
      />

      {/* ハイライト編集シート */}
      <HighlightSheet
        open={!!editingHighlight}
        onClose={() => setEditingHighlight(null)}
        highlight={editingHighlight}
        onSave={() => {}} // 新規作成は使わない
        onUpdate={handleUpdateHighlight}
        onDelete={handleDeleteHighlight}
      />

      <ImageReaderSettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
        settings={viewerSettings}
        onSettingsChange={handleSettingsChange}
      />

      {isLayoutPromptOpen && (
        <LayoutPrompt onDone={handleLayoutPromptDone}>
          <ReaderLayoutFields value={viewerSettings} onChange={applySettings} compact />
        </LayoutPrompt>
      )}
    </div>
  )
}
